import { useEffect, useMemo, useRef, useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { notifyParentOfDirtyState, requestNavigationConfirmation, listenToParentMessages } from '@/lib/app-messaging'

interface UseUnsavedChangesProps {
  forms: {
    [key: string]: UseFormReturn<any>
  }
}

export function useUnsavedChanges({ forms }: UseUnsavedChangesProps) {
  const pendingNavigationRef = useRef<(() => void) | null>(null)
  
  // Combine dirty state from all forms
  const isAnyFormDirty = useMemo(() => {
    return Object.values(forms).some(form => form.formState.isDirty)
  }, [
    // Watch each form's dirty state
    ...Object.values(forms).map(form => form.formState.isDirty)
  ])
  
  // Notify parent whenever dirty state changes
  useEffect(() => {
    notifyParentOfDirtyState(isAnyFormDirty)
    // Cleanup: clear the parent's dirty flag on unmount so it doesn't get stuck
    // if this component is destroyed without going through a normal save/discard flow.
    return () => notifyParentOfDirtyState(false)
  }, [isAnyFormDirty])
  
  // Listen for navigation confirmation responses from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { action?: string; confirmed?: boolean } | null
      
      if (!data || typeof data !== 'object') return
      
      if (data.action === 'NAVIGATION_CONFIRMATION_RESPONSE') {
        if (data.confirmed && pendingNavigationRef.current) {
          // User confirmed - proceed with navigation
          // Forms will be reset automatically when new data loads from API
          pendingNavigationRef.current()
          pendingNavigationRef.current = null
        } else {
          // User cancelled - clear pending navigation
          pendingNavigationRef.current = null
        }
      }
    }
    
    return listenToParentMessages(handleMessage)
  }, [])
  
  // Function to request confirmation before navigation
  const confirmNavigation = useCallback((onConfirm: () => void) => {
    if (isAnyFormDirty) {
      pendingNavigationRef.current = onConfirm
      requestNavigationConfirmation()
      return false // Block navigation
    }
    return true // Allow navigation
  }, [isAnyFormDirty])
  
  return {
    isAnyFormDirty,
    confirmNavigation
  }
}

